/**
 * Everyone in the tenant, and what an administrator can change about them.
 *
 * @module components/admin/TenantUserTable
 */

import React, { useEffect, useRef, useState } from "react";

import { TENANT_ROLE_OPTIONS } from "@/components/shared/role-labels";
import { invitationTone, inviteEmailTone } from "@/components/shared/status-tones";
import { TONE_BADGE } from "@/components/shared/tone-presentation";
import {
  Badge,
  Button,
  CollectionView,
  type Column,
  EmptyState,
  ErrorAlert,
  IconButton,
  MutedText,
  type RowAction,
  type RowActionsSpec,
  SelectField,
  StatusBadge,
  TextField,
  Toggle,
} from "@/components/ui";
import type { UserSummary } from "@/platform";
import type { EmailDeliveryStatus, InvitationStatus } from "@/server/types";

/**
 * Wording for each invite state.
 *
 * "Not signed in yet" rather than "Pending", because pending describes the
 * invitation record while what an administrator wants to know is whether this
 * person has ever arrived.
 */
const INVITE_LABELS: Record<InvitationStatus, string> = {
  pending: "Not signed in yet",
  accepted: "Signed in",
  revoked: "Invite revoked",
  expired: "Invite expired",
};

const EMAIL_LABELS: Record<EmailDeliveryStatus, string> = {
  queued: "Invite email queued",
  sending: "Invite email sending",
  sent: "Invite email sent",
  failed: "Invite email failed",
  suppressed: "Invite email suppressed",
};

/** Every write reloads these rows, so one waits for the last. */
const WAIT_REASON = "Wait for the change in progress to finish.";

const statusAction = (
  user: UserSummary,
  pending: boolean,
  onToggle: (user: UserSummary) => void,
): RowAction => {
  const active = user.status === "active";
  const base = {
    key: "status",
    label: active ? "Deactivate" : "Reactivate",
    icon: active ? "user-minus" : "user-check",
    // Deactivating signs someone out of every programme, so it is asked
    // about; reactivating gives nothing away.
    tone: active ? ("danger" as const) : ("default" as const),
    confirm: active
      ? {
          title: `Deactivate ${user.displayName}?`,
          body: "They can no longer sign in until an administrator reactivates them.",
          confirmLabel: "Deactivate user",
        }
      : (false as const),
    onSelect: () => {
      onToggle(user);
    },
    "data-testid": `tenant-user-status-${user.id}`,
  };
  return pending ? { ...base, disabled: true, disabledReason: WAIT_REASON } : base;
};

export interface TenantUserTableProps {
  users: readonly UserSummary[];
  /** True while any write is in flight; every control waits on it together. */
  pending: boolean;
  onChangeRole: (userId: string, role: string) => void;
  onToggleProgrammeCreation: (user: UserSummary) => void;
  onToggleStatus: (user: UserSummary) => void;
  onResendInvitation: (user: UserSummary) => void;
  onChangeEmail: (userId: string, email: string) => Promise<boolean>;
  /** What the last write to a row said when it failed. */
  error?: string | null;
}

/**
 * @example
 * ```tsx
 * <TenantUserTable users={users} pending={pending}
 *   onChangeRole={changeRole} … />
 * ```
 */
export function TenantUserTable({
  users,
  pending,
  onChangeRole,
  onToggleProgrammeCreation,
  onToggleStatus,
  onResendInvitation,
  onChangeEmail,
  error = null,
}: TenantUserTableProps): React.ReactElement {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  // The field replaces the Edit button and the button comes back in its place,
  // so each direction has to hand focus over: without it the reader is left on
  // the body and has to tab the table again.
  const previousEditingId = useRef<string | null>(null);
  useEffect(() => {
    const was = previousEditingId.current;
    previousEditingId.current = editingId;
    const target = editingId
      ? `tenant-user-email-input-${editingId}`
      : was && `tenant-user-email-edit-${was}`;
    if (target && was !== editingId) {
      document.querySelector<HTMLElement>(`[data-testid="${target}"]`)?.focus();
    }
  }, [editingId]);

  const save = (userId: string): void => {
    onChangeEmail(userId, editingValue).then((accepted) => {
      if (accepted) {
        setEditingId(null);
        setEditingValue("");
      }
    });
  };

  const columns: readonly Column<UserSummary>[] = [
    {
      key: "user",
      header: "User",
      render: (user) => (
        <div>
          <div className="font-semibold">{user.displayName}</div>
          {editingId === user.id ? (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <TextField
                id={`tenant-user-email-${user.id}`}
                type="email"
                size="sm"
                value={editingValue}
                onChange={setEditingValue}
                aria-label={`Email address for ${user.displayName}`}
                data-testid={`tenant-user-email-input-${user.id}`}
              />
              <Button
                size="sm"
                variant="primary"
                disabled={pending || editingValue.trim().length === 0}
                disabledReason={pending ? WAIT_REASON : "Enter an email address."}
                onClick={() => {
                  save(user.id);
                }}
                data-testid={`tenant-user-email-save-${user.id}`}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingId(null);
                  setEditingValue("");
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <MutedText as="span" size="caption">
                {user.email}
              </MutedText>
              <IconButton
                icon="pencil-simple"
                size="sm"
                onClick={() => {
                  setEditingId(user.id);
                  setEditingValue(user.email);
                }}
                aria-label={`Edit email address for ${user.displayName}`}
                data-testid={`tenant-user-email-edit-${user.id}`}
              />
            </div>
          )}
        </div>
      ),
      sortValue: (user) => user.displayName,
    },
    {
      key: "role",
      header: "Role",
      width: "min-w-48",
      render: (user) => (
        <SelectField
          id={`role-${user.id}`}
          value={user.accountRole}
          onChange={(value) => {
            onChangeRole(user.id, value);
          }}
          options={TENANT_ROLE_OPTIONS}
          // One write at a time, as the capability toggle and the email save
          // already are: they all touch these rows and each reloads them.
          disabled={pending}
          disabledReason={WAIT_REASON}
          size="sm"
          aria-label={`Role for ${user.displayName}`}
        />
      ),
    },
    {
      key: "creation",
      header: "Programme creation",
      width: "whitespace-nowrap",
      priority: 3,
      render: (user) => {
        // A client admin may always create programmes, so the toggle reports
        // that rather than a badge standing in for a control.
        const admin = user.accountRole === "client-admin";
        const inactive = user.status !== "active";
        let creationReason: string | undefined = WAIT_REASON;
        if (admin) {
          creationReason = "A client admin may always create programmes.";
        } else if (inactive) {
          creationReason = undefined;
        }
        return (
          <div className="flex flex-col items-start gap-1">
            <Toggle
              size="sm"
              checked={admin || (user.tenantCapabilities ?? []).includes("programme:create")}
              onChange={() => {
                onToggleProgrammeCreation(user);
              }}
              disabled={admin || pending || inactive}
              disabledReason={creationReason}
              aria-label={`Allow ${user.displayName} to create programmes`}
              aria-describedby={
                inactive && !admin ? `tenant-user-creation-note-${user.id}` : undefined
              }
              data-testid={`tenant-user-programme-creation-${user.id}`}
            />
            {inactive && !admin ? (
              <span id={`tenant-user-creation-note-${user.id}`}>
                <MutedText as="span" size="caption">
                  Reactivate the account to change this
                </MutedText>
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      priority: 2,
      render: (user) => (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={user.status} />
          {user.inviteStatus ? (
            <Badge
              size="sm"
              variant="soft"
              tone={TONE_BADGE[invitationTone(user.inviteStatus)]}
              data-testid={`tenant-user-invite-${user.id}`}
            >
              {INVITE_LABELS[user.inviteStatus]}
            </Badge>
          ) : null}
          {user.inviteEmailStatus ? (
            <Badge
              size="sm"
              variant="soft"
              tone={TONE_BADGE[inviteEmailTone(user.inviteEmailStatus)]}
              data-testid={`tenant-user-invite-email-${user.id}`}
            >
              {EMAIL_LABELS[user.inviteEmailStatus]}
            </Badge>
          ) : null}
        </div>
      ),
    },
  ];

  const rowActions: RowActionsSpec<UserSummary> = {
    label: (user) => user.displayName,
    testId: (user) => `tenant-user-actions-${user.id}`,
    items: (user) => [
      // Offered only while an invitation is still outstanding: somebody who
      // has signed in has nothing to resend.
      user.inviteStatus === "pending" && {
        key: "resend",
        label: "Resend invite",
        onSelect: () => {
          onResendInvitation(user);
        },
        "data-testid": `tenant-user-resend-invite-${user.id}`,
        ...(pending
          ? { disabled: true as const, disabledReason: WAIT_REASON }
          : { icon: "paper-plane-tilt", primary: true }),
      },
      statusAction(user, pending, onToggleStatus),
    ],
  };

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert data-testid="tenant-user-table-error">{error}</ErrorAlert> : null}
      <CollectionView
        storageKey="tenant-user-table"
        caption="Tenant users"
        columns={columns}
        rows={users}
        rowKey={(user) => user.id}
        rowActions={rowActions}
        emptyState={
          <EmptyState
            icon="users-three"
            title="No users in this tenant yet"
            description="Invite someone and they appear here."
            density="row"
          />
        }
        data-testid="tenant-user-table"
      />
    </div>
  );
}
